export type SignOutWarningKind = "icd" | "pending-stain" | "other";

export interface SignOutIssue {
  specimenNumber: string;
  field: string;
  message: string;
  severity: "error" | "warning";
}

export interface SignOutWarning {
  specimenNumber: string;
  field: string;
  message: string;
  kind: SignOutWarningKind;
}

export interface SignOutWarningPresentation {
  title:
    | "Cannot Sign Out"
    | "Missing ICD-10 Codes Warning"
    | "Pending Stains Warning"
    | "Sign-Out Warnings";
  canOverride: boolean;
  blockingIssues: SignOutIssue[];
  warnings: SignOutWarning[];
  warningsByKind: Record<SignOutWarningKind, SignOutWarning[]>;
}

const GROSS_TEMPLATE_GUIDANCE =
  "Some fields are missing or have not been filled out yet in the gross description. Go to the Grossing tab on the Pathologist Review page and update them.";

export const getGrossTemplateSignOutGuidance = (
  issues: ReadonlyArray<SignOutIssue>
): string | undefined =>
  issues.some(
    (issue) =>
      issue.severity === "error" &&
      ((issue.field === "Gross Description" &&
        issue.message.startsWith("Unresolved template variable(s):")) ||
        issue.message.startsWith(
          "Gross Description contains unresolved template variable(s):"
        ))
  )
    ? GROSS_TEMPLATE_GUIDANCE
    : undefined;

export const hasWholeCaseIcdCode = (
  icdCodes: ReadonlyArray<string | null | undefined>
): boolean => icdCodes.some((code) => Boolean(code?.trim()));

export const toSignOutWarning = (
  issue: Pick<SignOutIssue, "specimenNumber" | "field" | "message">
): SignOutWarning => ({
  specimenNumber: issue.specimenNumber,
  field: issue.field,
  message: issue.message,
  kind:
    issue.field === "ICD Code"
      ? "icd"
      : issue.field === "Pending Stains"
      ? "pending-stain"
      : "other",
});

const parseBackendSpecimenIssue = (
  issue: string
): { specimenNumber: string; message: string } => {
  const match = issue.match(/^Specimen ([A-Z]+):\s*(.+)$/);
  return match
    ? { specimenNumber: match[1], message: match[2] }
    : { specimenNumber: "", message: issue };
};

export const normalizeBackendSignOutWarning = (
  issue: string
): SignOutWarning => {
  const parsed = parseBackendSpecimenIssue(issue);
  if (parsed.message === "No ICD code selected") {
    return { ...parsed, field: "ICD Code", kind: "icd" };
  }
  if (/^\d+ pending stain\(s\).*not yet completed$/.test(parsed.message)) {
    return { ...parsed, field: "Pending Stains", kind: "pending-stain" };
  }
  return { ...parsed, field: "", kind: "other" };
};

export const buildSignOutWarningPresentation = (
  blockingIssues: SignOutIssue[],
  warnings: SignOutWarning[]
): SignOutWarningPresentation => {
  const warningsByKind: Record<SignOutWarningKind, SignOutWarning[]> = {
    icd: warnings.filter((warning) => warning.kind === "icd"),
    "pending-stain": warnings.filter(
      (warning) => warning.kind === "pending-stain"
    ),
    other: warnings.filter((warning) => warning.kind === "other"),
  };

  if (blockingIssues.length > 0) {
    return {
      title: "Cannot Sign Out",
      canOverride: false,
      blockingIssues,
      warnings,
      warningsByKind,
    };
  }

  const hasIcd = warningsByKind.icd.length > 0;
  const hasPendingStains = warningsByKind["pending-stain"].length > 0;
  const hasOther = warningsByKind.other.length > 0;
  const title =
    hasIcd && !hasPendingStains && !hasOther
      ? "Missing ICD-10 Codes Warning"
      : hasPendingStains && !hasIcd && !hasOther
      ? "Pending Stains Warning"
      : "Sign-Out Warnings";

  return {
    title,
    canOverride: warnings.length > 0,
    blockingIssues,
    warnings,
    warningsByKind,
  };
};
